import {
  Component,
  Input,
  OnInit,
  inject,
  OnDestroy,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover'; // Added popover
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzProgressModule } from 'ng-zorro-antd/progress';

import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import { NotificationService } from '@rapider/services';

import { ChatInteractionWidgetComponent } from '../chat-interaction-widget/chat-interaction-widget.component';
import { ChatToolCallCardComponent } from '../chat-tool-call-card/chat-tool-call-card.component';
import { ChatAsyncProgressCardComponent } from '../chat-async-progress-card/chat-async-progress-card.component';
import { AgentDebugDrawerComponent } from '../agent-debug-drawer/agent-debug-drawer.component';

import { BrowserStorageManagementService } from '@rapider/services';
import { environment } from '@rapider/environments';

import { ChatEndpointActionCardComponent } from '../chat-endpoint-action-card/chat-endpoint-action-card.component';
import { ChatEntityDataCardComponent } from '../chat-entity-data-card/chat-entity-data-card.component';
import { ChatWorkflowPlanCardComponent } from '../chat-workflow-plan-card/chat-workflow-plan-card.component';
import { TemplateRendererComponent } from '@rapider/template-renderer';

import { AiChatActions } from '../state/ai-chat.actions';
import {
  selectProjectModels,
  selectLoadingModels,
  selectActiveThreadId,
  selectMessages,
  selectThreads,
} from '../state/ai-chat.reducer';

import {
  ProjectDataActions,
  selectProjectModelsList,
} from '@rapider/project-data';

import {
  AiAction,
  WorkflowPlan,
  getActionButtonLabels,
  hasEndpointAction,
  pureAddStringToSet,
  pureRemoveStringFromSet,
  pureAddMultipleStringsToSet,
  getOperationIconAndColor,
  getHttpMethodColorClass,
  getActiveActions,
  buildExecuteAllTooltip,
  getWorkflowTaskTheme,
  isActionReExecutable,
  generateTempId,
  FileAttachment,
  InteractionOption,
  forceDownloadFile,
  isBrowserPreviewable,
  isServerAction,
} from '../ai-chat.config';
import { WorkflowExecutionService } from '../services/workflow-execution.service';

import {
  AIChatMessage,
  formatMessageTime,
  getFileIconClass,
  formatFileSize,
  AI_CHAT_CONFIG,
  MemoryUpdate,
  buildMemoryNoteQueryParams,
  memoryNoteLabel,
  createPlayableAudioUrl,
  extractListGridConfig,
  generateDefaultListGridConfig,
  normalizePayloadToArray,
} from '../ai-chat.config';

/**
 * @class ChatMessageBubbleComponent
 * @description Renders individual chat messages, encapsulating robust rendering logic and resolving specific action states.
 */
@Component({
  selector: 'rapider-chat-message-bubble',
  standalone: true,
  imports: [
    CommonModule,
    NzPopconfirmModule,
    NzPopoverModule,
    MarkdownPreviewComponent,
    ChatEndpointActionCardComponent,
    ChatEntityDataCardComponent,
    ChatWorkflowPlanCardComponent,
    TemplateRendererComponent,
    AgentDebugDrawerComponent,
    ChatInteractionWidgetComponent,
    ChatToolCallCardComponent,
    ChatAsyncProgressCardComponent,
    NzTooltipModule,
    NzButtonModule,
    NzTagModule,
    NzModalModule,
    NzTimelineModule,
    NzProgressModule,
  ],
  templateUrl: './chat-message-bubble.component.html',
})
export class ChatMessageBubbleComponent
  implements OnInit, OnDestroy, OnChanges
{
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject(Store);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly workflowExecutionService = inject(WorkflowExecutionService);

  @Input({ required: true }) message!: AIChatMessage;
  @Input() isLatest: boolean = false;

  // Create a reactive signal for the message input to trigger computed values
  public messageSignal = signal<AIChatMessage | null>(null);

  public getActionButtonLabels = getActionButtonLabels;
  public hasEndpointAction = hasEndpointAction;
  public getOperationIconAndColor = getOperationIconAndColor;
  public getHttpMethodColorClass = getHttpMethodColorClass;
  public getTaskTheme = getWorkflowTaskTheme;
  public isActionReExecutable = isActionReExecutable;

  public readonly config = AI_CHAT_CONFIG;

  public discardedActionIndices = signal<Set<number>>(new Set());
  public collapsedActionIndices = signal<Set<number>>(new Set()); // New Collapse Management
  private seenActionIndices = new Set<number>(); // Tracks actions we've already initialized for collapse state
  public isWorkflowStepCollapsed = signal<boolean>(true); // Keeps the workflow step prompt collapsed by default
  public isLogsExpanded = signal<boolean>(false); // NEW: Controls the visibility of execution logs

  public isLoadingAudio = signal<boolean>(false);
  public audioError = signal<boolean>(false);
  public autoPlayAudio = signal<boolean>(false);
  public localVoiceUrl = signal<string | undefined>(undefined);
  public getIcon = getFileIconClass;
  public formatSize = formatFileSize;

  public get visibleAttachments(): FileAttachment[] {
    const attachments = this.message?.attachments || [];
    if (!attachments.length) return [];
    if (!this.message?.isVoiceMessage) return attachments;

    // Filter out the audio attachment corresponding to the voice message
    return attachments.filter((file) => {
      if (this.message.voiceFileId && file.id === this.message.voiceFileId)
        return false;
      const isAudioType =
        file.mimeType?.startsWith('audio/') ||
        file.mimeType?.startsWith('video/ogg');
      const isAudioExt =
        file.name?.endsWith('.wav') ||
        file.name?.endsWith('.mp3') ||
        file.name?.endsWith('.m4a') ||
        file.name?.endsWith('.ogg');
      if (!this.message.voiceFileId && (isAudioType || isAudioExt))
        return false;
      return true;
    });
  }

  // Message Action Signals
  public isCopied = signal<boolean>(false);
  public isTranscriptCopied = signal<boolean>(false);
  public isReasoningCollapsed = signal<boolean>(true);

  // Attachment Preview State
  public isPreviewModalVisible = signal<boolean>(false);
  public previewFile = signal<FileAttachment | null>(null);

  /**
   * Evaluates if a file attachment can be natively rendered in a browser.
   * Uses the pure function `isBrowserPreviewable` to determine capability.
   * @param {FileAttachment} file - The file to evaluate.
   * @returns {boolean} True if previewable.
   */
  public isPreviewable(file: FileAttachment): boolean {
    return isBrowserPreviewable(
      file.mimeType,
      file.name,
      file.extension,
      file.url,
    );
  }

  /**
   * Triggers a forced download of the file attachment by fetching it as a Blob.
   * This handles cross-origin (CORS) S3 downloads preventing files from simply opening.
   * @param {FileAttachment} file - The file to download.
   * @param {Event} event - The DOM click event to prevent bubbling.
   * @returns {Promise<void>}
   */
  public async triggerDownload(
    file: FileAttachment,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (file.url) {
      await forceDownloadFile(file.url, file.name);
    }
  }

  /**
   * Opens the file attachment in a new browser tab.
   * Ideal for PDFs, HTML, or images that the user wants to keep open as a reference.
   * @param {FileAttachment} file - The file to open.
   * @param {Event} event - The DOM click event to prevent bubbling.
   */
  public openInNewTab(file: FileAttachment, event: Event): void {
    event.stopPropagation();
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }

  /**
   * Copies the raw message text to the clipboard and triggers a temporary success state.
   * @returns {void}
   */
  public copyMessageText(): void {
    if (!this.message.text) return;
    navigator.clipboard.writeText(this.message.text).then(() => {
      this.isCopied.set(true);
      setTimeout(() => this.isCopied.set(false), 2000);
    });
  }

  /**
   * Copies the transcript text of a voice message to the clipboard.
   * @returns {void}
   */
  public copyTranscriptText(): void {
    if (!this.message.text) return;
    navigator.clipboard.writeText(this.message.text).then(() => {
      this.isTranscriptCopied.set(true);
      setTimeout(() => this.isTranscriptCopied.set(false), 2000);
    });
  }

  /**
   * Saves the AI message content to spaces.
   */
  public saveToSpace(): void {
    if (!this.message.text) return;

    const activeThreadId = this.store.selectSignal(selectActiveThreadId)();
    const threads = this.store.selectSignal(selectThreads)();
    const activeThread = threads.find((t) => t.id === activeThreadId);
    const threadTitle = activeThread?.title || 'Unknown Thread';

    const now = new Date();
    const dateOpts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    const timeOpts: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
    };
    const formattedDate = now.toLocaleDateString('en-US', dateOpts);
    const formattedTime = now
      .toLocaleTimeString('en-US', timeOpts)
      .toLowerCase()
      .replace(' ', '');

    let contentToSave = `${this.message.text}\n\n`;

    if (
      this.message.entityData &&
      Array.isArray(this.message.entityData.data) &&
      this.message.entityData.data.length > 0
    ) {
      const data = this.message.entityData.data;
      const headers = Array.from(
        new Set(data.flatMap((item: any) => Object.keys(item))),
      );

      contentToSave += `**Data Results: ${this.message.entityData.entityName}**\n\n`;
      contentToSave += `| ${headers.join(' | ')} |\n`;
      contentToSave += `| ${headers.map(() => '---').join(' | ')} |\n`;

      data.forEach((item: any) => {
        const row = headers.map((header) => {
          const val = item[header];
          if (val === null || val === undefined) return '';
          if (typeof val === 'object')
            return JSON.stringify(val).replace(/\|/g, '\\|');
          return String(val).replace(/\|/g, '\\|');
        });
        contentToSave += `| ${row.join(' | ')} |\n`;
      });
      contentToSave += '\n';
    }

    contentToSave += `> Saved from "${threadTitle}" on ${formattedDate} at ${formattedTime}`;

    this.browserStorage.setItem('pendingAiSaveContent', contentToSave);
    this.router.navigate(['/knowledge/save-to-page']);
  }

  /**
   * Opens a note the runtime saved during this turn in the Knowledge notes view (the private
   * knowledge view for notes saved to the person's own space).
   *
   * @param note The saved note (`message.memoryUpdates` entry).
   */
  public openMemoryNote(note: MemoryUpdate): void {
    this.router.navigate([...this.config.constants.knowledgeNotesRoute], { queryParams: buildMemoryNoteQueryParams(note) });
  }

  /**
   * PURE FUNCTION: label of a saved-note chip (company vs private knowledge).
   *
   * @param note The saved note.
   * @returns The localized chip label.
   */
  public memoryLabel(note: MemoryUpdate): string {
    return memoryNoteLabel(note);
  }

  /**
   * Toggles the visibility of the AI reasoning/thinking block natively.
   * @returns {void}
   */
  public toggleReasoning(): void {
    this.isReasoningCollapsed.update((v) => !v);
  }

  /**
   * PURE FUNCTION: Derives the title of the reasoning block based on current step status.
   * @returns {string} The active step title, completion summary, or default fallback.
   */
  public getReasoningHeaderTitle(): string {
    if (
      !this.message.reasoningSteps ||
      this.message.reasoningSteps.length === 0
    ) {
      return this.config.texts.reasoningTitle;
    }
    const activeStep = this.message.reasoningSteps.find(
      (s) => s.status === 'active' || s.status === 'pending',
    );
    if (activeStep) {
      return activeStep.title || 'Processing...';
    }
    return `Planned in ${this.message.reasoningSteps.length} steps`;
  }

  /**
   * PURE FUNCTION: Checks if the reasoning timeline is actively running.
   * @returns {boolean} True if a step is currently pending or active.
   */
  public isReasoningActive(): boolean {
    if (
      !this.message.reasoningSteps ||
      this.message.reasoningSteps.length === 0
    )
      return false;
    return this.message.reasoningSteps.some(
      (s) => !s.status || s.status === 'active' || s.status === 'pending',
    );
  }

  /**
   * Determines if a file is an image and opens the preview modal, or initiates a download.
   * Creates safe object URLs for local File blobs if missing an external URL.
   * @param {FileAttachment} file - The file to preview or download.
   */
  public handleAttachmentClick(file: FileAttachment): void {
    if (file.mimeType.startsWith('image/')) {
      if (file.url || file.file) {
        let previewFileToSet = file;
        if (!file.url && file.file) {
          // Create a shallow copy to avoid mutating the frozen NgRx state object
          previewFileToSet = { ...file, url: URL.createObjectURL(file.file) };
        }
        this.previewFile.set(previewFileToSet);
        this.isPreviewModalVisible.set(true);
      }
    } else {
      this.downloadAttachment(file);
    }
  }

  /**
   * Closes the attachment preview modal and cleans up active selections.
   */
  public closeAttachmentPreview(): void {
    this.isPreviewModalVisible.set(false);
    this.previewFile.set(null);
  }

  /**
   * Dispatches the user's structured interaction selection (e.g., Radio/Checkbox) back to the store.
   * @param {InteractionOption[]} selectedOptions - The options the user selected in the widget.
   */
  public submitInteraction(selectedOptions: InteractionOption[]): void {
    if (!this.message.interactionConfig) return;
    this.store.dispatch(
      AiChatActions.sendInteractionResponse({
        messageId: this.message.id,
        allOptions: this.message.interactionConfig.options,
        selectedOptions,
      }),
    );
  }

  /**
   * Dispatches an explicit skip command for the interactive widget back to the store.
   */
  public skipInteraction(): void {
    this.store.dispatch(
      AiChatActions.skipInteraction({ messageId: this.message.id }),
    );
  }

  /**
   * Triggers a native browser download of the attachment payload.
   * @param {FileAttachment} file - The file to download.
   */
  public downloadAttachment(file: FileAttachment): void {
    if (file.url) {
      const a = document.createElement('a');
      a.href = file.url;
      a.download = file.name;
      a.click();
    } else if (file.file) {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  /**
   * PURE FUNCTION: Checks if the workflow plan has been explicitly confirmed by evaluating subsequent messages.
   * Scans ahead in the thread array for an action-response flagged as a confirmation.
   * @param {AIChatMessage | null} currentMsg - The message currently rendering the workflow plan.
   * @param {AIChatMessage[]} allMessages - Array of all messages currently loaded in the thread.
   * @returns {boolean} True if a subsequent confirmation message is found.
   */
  private checkWorkflowConfirmation(
    currentMsg: AIChatMessage | null,
    allMessages: AIChatMessage[],
  ): boolean {
    if (!currentMsg || !currentMsg.workflowPlan) return false;

    const currentIndex = allMessages.findIndex((m) => m.id === currentMsg.id);
    if (currentIndex === -1) return false;

    // Scan subsequent messages in the array
    for (let i = currentIndex + 1; i < allMessages.length; i++) {
      const nextMsg = allMessages[i];
      if (nextMsg.type === 'action-response' && nextMsg.isConfirmation) {
        return true;
      }
    }
    return false;
  }

  /**
   * COMPUTED: Resolves if the current workflow plan has been executed/confirmed.
   * Reactively updates when new messages are added to the NgRx store.
   */
  public isWorkflowConfirmed = computed(() => {
    return this.checkWorkflowConfirmation(
      this.messageSignal(),
      this.store.selectSignal(selectMessages)(),
    );
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message']) {
      this.messageSignal.set(this.message);

      if (this.message.actions && this.message.actions.length > 0) {
        let added = false;
        const currentCollapsed = new Set(this.collapsedActionIndices());
        this.message.actions.forEach((action, i) => {
          if (!this.seenActionIndices.has(i)) {
            this.seenActionIndices.add(i);
            // Default to collapsed when first seen
            currentCollapsed.add(i);
            added = true;
          }
        });
        if (added) {
          this.collapsedActionIndices.set(currentCollapsed);
        }
      }
    }
  }

  public getFormattedTime(): string {
    return formatMessageTime(this.message.timestamp);
  }

  public activeActions = computed(() => {
    const msg = this.messageSignal();
    return getActiveActions(msg?.actions, this.discardedActionIndices());
  });

  // NEW: Filter active actions to only those that haven't been executed yet
  public pendingActions = computed(() => {
    return this.activeActions().filter(
      (a) => !a.executionState || a.executionState.status === 'idle',
    );
  });

  public areAllCollapsed = computed(() => {
    const msg = this.messageSignal();
    if (!msg?.actions || msg.actions.length === 0) return false;
    const activeIndices = msg.actions
      .map((a, i) => i)
      .filter((i) => !this.discardedActionIndices().has(i));
    if (activeIndices.length === 0) return false;
    return activeIndices.every((i) => this.collapsedActionIndices().has(i));
  });

  public isDebugDrawerOpen = signal(false);

  public actionResponseDataFromStorage = computed(() => {
    const msg = this.messageSignal();
    if (msg?.type !== 'action-response') return null;

    const entityName = msg.actionResponseData?.entityName;
    if (!entityName) return null;

    // We need the active thread ID to query the correct context memory
    const threadId = this.store.selectSignal(selectActiveThreadId)();
    const key = `workflow_context_${threadId || 'transient-thread'}`;
    const context = this.browserStorage.getItem<Record<string, any>>(key);

    if (!context) return null;

    // Resolves the standard lowercase key used by WorkflowExecutionService
    const fallbackKey =
      entityName.charAt(0).toLowerCase() + entityName.slice(1);
    const data = context[fallbackKey];

    // Normalize to array for the grid component
    return data ? normalizePayloadToArray(data) : null;
  });

  ngOnInit(): void {
    if (this.message.entityData?.entityName) {
      const name = this.message.entityData.entityName;

      // Check if it's already in the global consolidated project models store
      const globalModels = this.store.selectSignal(selectProjectModelsList)();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existsGlobally = globalModels.some(
        (m: any) =>
          (m.name && m.name.toLowerCase() === name.toLowerCase()) ||
          (m.dataTableName &&
            m.dataTableName.toLowerCase() === name.toLowerCase()),
      );

      // Check local caches and loading states
      const cachedModels = this.store.selectSignal(selectProjectModels)();
      const cachedLoadings = this.store.selectSignal(selectLoadingModels)();

      if (!existsGlobally && !cachedModels[name] && !cachedLoadings[name]) {
        // DISPATCH SYNCHRONOUSLY WITHOUT SETTIMEOUT
        // Removing the setTimeout() ensures the NgRx reducer processes this immediately
        // and sets the 'loading' flag to true before the next chat bubble evaluates its ngOnInit
        // during an *ngFor / @for render cycle, effectively eliminating duplicate API calls.
        this.store.dispatch(
          AiChatActions.loadProjectModel({ entityName: name }),
        );
      }
    }
  }

  public isGridLoading = computed(() => {
    const entityName = this.message.entityData?.entityName;
    if (!entityName) return false;
    return this.store.selectSignal(selectLoadingModels)()[entityName] || false;
  });

  public gridConfig = computed(() => {
    const entityData = this.message.entityData;
    if (!entityData) return null;

    const entityName = entityData.entityName;
    const model = this.store.selectSignal(selectProjectModels)()[entityName];
    const loading = this.store.selectSignal(selectLoadingModels)()[entityName];

    if (model) {
      const configObj = extractListGridConfig(model);
      if (configObj) return configObj;
    }

    if (!loading) {
      return generateDefaultListGridConfig(entityData.data);
    }
    return null;
  });

  ngOnDestroy(): void {
    const url = this.localVoiceUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  public loadAudioBlob(): void {
    if (!this.message.voiceFileId || this.isLoadingAudio()) return;

    this.isLoadingAudio.set(true);
    this.audioError.set(false);
    this.autoPlayAudio.set(true);

    const fetchUrl = `${environment.apiBaseUrl}/project-files/get-file-content/${this.message.voiceFileId}`;

    this.http.get(fetchUrl, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        try {
          this.localVoiceUrl.set(createPlayableAudioUrl(blob));
          this.isLoadingAudio.set(false);
        } catch (e) {
          console.error('Failed to create Object URL:', e);
          this.handleAudioError();
        }
      },
      error: (err) => {
        console.error('Failed to fetch audio blob:', err);
        if (err && err.status === 200 && err.error?.text) {
          try {
            this.localVoiceUrl.set(createPlayableAudioUrl(err.error.text));
            this.isLoadingAudio.set(false);
            return;
          } catch (e) {
            console.error('Failed to salvage blob from HttpErrorResponse:', e);
          }
        }
        this.handleAudioError();
      },
    });
  }

  private handleAudioError(): void {
    this.isLoadingAudio.set(false);
    this.audioError.set(true);
    this.notificationService.createNotification(
      'warning',
      'Audio Unavailable',
      this.config.texts.voiceExpiredError,
    );
  }

  public executeAction(action: AiAction): void {
    if (action.type !== 'endpoint' && !isServerAction(action)) return;

    this.store.dispatch(
      AiChatActions.executeEndpointActions({
        actions: [action],
        messageId: this.message.id,
        originatingRequestMessageId: this.message.originatingRequestMessageId,
      }),
    );
  }

  public discardActions(): void {
    this.store.dispatch(
      AiChatActions.dismissActions({ messageId: this.message.id }),
    );
  }

  public discardAction(index: number): void {
    if (!this.message.actions) return;
    if (index !== -1) {
      const action = this.message.actions[index];
      this.discardedActionIndices.update((current) => {
        const newSet = new Set(current);
        newSet.add(index);
        return newSet;
      });

      // If it's a server action, report the cancellation decision to the backend
      if (action && isServerAction(action)) {
        const activeThreadId = this.store.selectSignal(selectActiveThreadId)();
        this.workflowExecutionService.cancelServerAction(
          action,
          this.message.id,
          activeThreadId,
          index,
          this.message.originatingRequestMessageId,
        );
      }
    }
  }

  public isActionDiscarded(index: number): boolean {
    if (!this.message.actions) return false;
    return index !== -1 && this.discardedActionIndices().has(index);
  }

  public undoDiscardAction(index: number): void {
    if (!this.message.actions) return;
    if (index !== -1) {
      this.discardedActionIndices.update((current) => {
        const newSet = new Set(current);
        newSet.delete(index);
        return newSet;
      });
    }
  }

  public discardAllActiveActions(): void {
    if (!this.message.actions) return;
    const pending = this.pendingActions();
    const pendingIndices = pending.map((a) => this.message.actions!.indexOf(a));
    this.discardedActionIndices.update((current) => {
      const newSet = new Set(current);
      pendingIndices.forEach((idx) => newSet.add(idx));
      return newSet;
    });

    // Cancel any pending server actions on the backend
    const activeThreadId = this.store.selectSignal(selectActiveThreadId)();
    pending.forEach((action) => {
      if (isServerAction(action)) {
        const idx = this.message.actions!.indexOf(action);
        this.workflowExecutionService.cancelServerAction(
          action,
          this.message.id,
          activeThreadId,
          idx,
          this.message.originatingRequestMessageId,
        );
      }
    });
  }

  public collapseAllActions(): void {
    if (!this.message.actions) return;
    const pendingIndices = this.pendingActions().map((a) =>
      this.message.actions!.indexOf(a),
    );
    this.collapsedActionIndices.update((current) => {
      const newSet = new Set(current);
      pendingIndices.forEach((idx) => newSet.add(idx));
      return newSet;
    });
  }

  public expandAllActions(): void {
    this.collapsedActionIndices.set(new Set());
  }

  public toggleWorkflowStepCollapse(): void {
    this.isWorkflowStepCollapsed.update((v) => !v);
  }

  public onToggleCollapse(index: number): void {
    this.collapsedActionIndices.update((current) => {
      const newSet = new Set(current);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }

  public onShowInCanvas(): void {
    const entityData = this.message.entityData;
    if (!entityData) return;

    // FIX: Extract Record IDs dynamically (Support MongoDB _id and SQL id)
    const recordIds = entityData.data
      .map((r: any) => r.id || r._id)
      .filter(Boolean);

    // Map into the standard payload expected by the DataInsightComponent
    const insightPayload = {
      id: this.message.id,
      timestamp: this.message.timestamp,
      operationType: 'READ', // Standard viewing maps to a READ operation
      title: `${entityData.entityName}`,
      description: this.message.text,
      projectModelName: entityData.entityName,
      recordIds: recordIds,
      isSuccess: true,
      payloadData: null,
    };

    // Dispatch cross-domain signal to the Main Canvas
    this.store.dispatch(
      ProjectDataActions.setActiveInsight({ insight: insightPayload }),
    );
  }

  /**
   * Action triggered when clicking the centered tag for an Action Response.
   * Shows the resulting entity directly in the canvas.
   */
  public showActionResponseInCanvas(): void {
    const actionData = this.message.actionResponseData;
    if (!actionData) return;

    const entityName = actionData.entityName;
    const responsePayload = actionData.response;

    // Safely extract the record ID supporting array or object payloads
    let recordId = null;
    if (responsePayload) {
      if (Array.isArray(responsePayload) && responsePayload.length > 0) {
        recordId = responsePayload[0].id || responsePayload[0]._id;
      } else {
        recordId = responsePayload.id || responsePayload._id;
      }
    }

    if (!entityName || !recordId) return;

    const insightPayload = {
      id: generateTempId(),
      timestamp: this.message.timestamp,
      operationType: 'READ',
      title: entityName,
      description: this.message.text,
      projectModelName: entityName,
      recordIds: [recordId],
      isSuccess: actionData.actionResponseStatus === 'success',
      payloadData: null,
    };

    this.store.dispatch(
      ProjectDataActions.setActiveInsight({ insight: insightPayload }),
    );
  }

  public executeAllActiveActions(): void {
    const actionsToExecute = this.pendingActions();
    if (actionsToExecute.length === 0) return;

    this.store.dispatch(
      AiChatActions.executeEndpointActions({
        actions: actionsToExecute,
        messageId: this.message.id,
        originatingRequestMessageId: this.message.originatingRequestMessageId,
      }),
    );
  }

  public executeWorkflowPlan(plan: WorkflowPlan): void {
    if (!plan) return;

    this.store.dispatch(
      AiChatActions.sendMessage({
        text: $localize`:@@aiChat.userConfirmedExecution:User confirmed execution.`,
        messageType: 'action-response',
        isConfirmation: true,
      } as any),
    );

    // CRITICAL FIX: Yield the event loop for 50ms so the optimistic "User confirmed execution"
    // message is fully painted to the NgRx store and DOM *before* the workflow engine fires the first step.
    setTimeout(() => {
      this.store.dispatch(
        AiChatActions.executeWorkflowPlan({
          plan: plan,
          messageId: this.message.id,
        }),
      );
    }, 50);
  }

  public executeJsonata(): void {
    if (this.message.jsonataTransformation) {
      this.store.dispatch(
        AiChatActions.executeJsonata({
          messageId: this.message.id,
          expression: this.message.jsonataTransformation,
        }),
      );
    }
  }
  public onFetchLogs(correlationId: string): void {
    if (correlationId) {
      this.store.dispatch(
        AiChatActions.fetchWorkflowLogs({
          messageId: this.message.id,
          correlationId,
        }),
      );
    }
  }
}
